// src/context/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { API_BASE } from '../api/config';

interface UserClaims {
  sub: string;
  email: string;
  isAdmin?: boolean;
  isContractor?: boolean;
  isModerator?: boolean;
}

interface AuthContextType {
  currentUser: UserClaims | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (email: string, password: string, isContractor: boolean, privacyPolicyVersion?: string, marketingOptIn?: boolean, contractorData?: { companyName?: string; categoryIds?: number[] }) => Promise<{ success: boolean; message?: string }>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_BASE = `${API_BASE}/api/Auth`;

async function fetchMe(): Promise<UserClaims | null> {
  try {
    const res = await fetch(`${AUTH_BASE}/me`, { credentials: 'include' });
    if (!res.ok) return null;
    return await res.json() as UserClaims;
  } catch {
    return null;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [currentUser, setCurrentUser] = useState<UserClaims | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore auth state from the HttpOnly cookie via the /me endpoint
  useEffect(() => {
    fetchMe().then(user => {
      setCurrentUser(user);
      setLoading(false);
    });
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await fetch(`${AUTH_BASE}/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, message: errorData.Message || 'Login failed.' };
      }

      // Cookie is now set by the server; fetch user claims
      const user = await fetchMe();
      setCurrentUser(user);
      return { success: true };
    } catch (error: any) {
      return { success: false, message: error.message || 'An unexpected error occurred during login.' };
    }
  };

  const register = async (email: string, password: string, isContractor: boolean, privacyPolicyVersion = '1.0', marketingOptIn = false, contractorData?: { companyName?: string; categoryIds?: number[] }) => {
    try {
      const response = await fetch(`${AUTH_BASE}/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email,
          password,
          isContractor,
          consentGiven: true,
          privacyPolicyVersion,
          marketingOptIn,
          ...(isContractor && contractorData ? contractorData : {}),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        return { success: false, message: errorData.Message || 'Registration failed.' };
      }

      return { success: true, message: 'Registration successful. Please log in.' };
    } catch (error: any) {
      return { success: false, message: error.message || 'An unexpected error occurred during registration.' };
    }
  };

  const logout = async () => {
    await fetch(`${AUTH_BASE}/logout`, { method: 'POST', credentials: 'include' });
    setCurrentUser(null);
  };

  return (
    <AuthContext.Provider value={{ currentUser, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
